import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, ChevronRight, Loader2, MessageSquareWarning } from "lucide-react";
import { toast } from "sonner";
import api, { apiErrorMessage } from "../lib/api";
import { browserLabel } from "../lib/tickets";
import {
  PORTAL_RULE, REPORT_IMPACT, REPORT_IMPACT_ORDER, REPORT_KIND, REPORT_KIND_ORDER, REPORT_STATUS, SCREENSHOT_WARNING,
  groupFromPath, prefillFrom, sentLine,
} from "../lib/portalTickets";
import { Chip, fieldCls, primaryBtn, secondaryBtn, Shell } from "../components/portal/PortalShell";
import ScreenshotPicker, { uploadScreenshots } from "../components/admin/tickets/ScreenshotPicker";

function day(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString("ro-RO", { day: "numeric", month: "short", year: "numeric" });
}

/**
 * `/portal/raportari` — "Raportările mele" (14.10.2026): what this person
 * reported about the portal, and the form to report something new. Reached
 * from "Raportează o problemă" at the foot of every page, which opens the
 * form (`?nou=1`) knowing the page it came from (`&din=`).
 */
export default function PortalReports() {
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState(null);
  const [error, setError] = useState("");
  const [open, setOpen] = useState(params.get("nou") === "1");
  const from = params.get("din") || "";
  const [start] = useState(() => prefillFrom(params));

  const load = useCallback(async () => {
    try {
      setItems((await api.get("/portal/tickets")).data.items || []);
      setError("");
    } catch (err) {
      setError(err?.response?.status === 401 ? "out" : apiErrorMessage(err, "Nu am putut încărca raportările."));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const close = () => {
    setOpen(false);
    if (params.get("nou")) setParams({}, { replace: true });
  };

  return (
    <Shell wide report={false}>
      <Link to={from || "/portal"} className="inline-flex items-center gap-1.5 text-sm text-[#6B7280] hover:text-[#965EA7]">
        <ArrowLeft size={14} /> Înapoi
      </Link>
      <h1 className="mt-6 font-display text-3xl font-bold tracking-tight text-[#373A45]">Raportările mele</h1>
      <p className="mt-2 text-[15px] leading-relaxed text-[#6B7280]">{PORTAL_RULE}</p>

      {error === "out" && (
        <p className="mt-8 text-sm text-[#373A45]">
          Sesiunea s-a încheiat. <Link to="/portal" className="font-semibold text-[#965EA7]">Intră din nou</Link>.
        </p>
      )}
      {error && error !== "out" && <p className="mt-8 text-sm text-[#c0483f]" role="alert">{error}</p>}

      {!error && (open ? (
        <ReportForm from={from} start={start} onClose={close} onSent={() => { close(); load(); }} />
      ) : (
        <button type="button" onClick={() => setOpen(true)} className={`${primaryBtn} mt-6`} data-testid="portal-report-open">
          <MessageSquareWarning size={16} /> Raportează o problemă
        </button>
      ))}

      {!error && items === null && <div className="py-16 text-center text-[#6B7280]"><Loader2 className="mx-auto animate-spin" /></div>}
      {items && (
        items.length === 0 ? (
          !open && <p className="mt-8 text-sm text-[#6B7280]" data-testid="portal-reports-empty">Încă nu ai trimis nicio raportare.</p>
        ) : (
          <ul className="mt-8 grid gap-3" data-testid="portal-reports-list">
            {items.map((t) => (
              <li key={t.id}>
                <Link to={`/portal/raportari/${t.id}`}
                  className="flex items-center gap-3 rounded-2xl border border-[#E5E7EB] bg-white p-4 hover:border-[#965EA7]">
                  <span className="font-mono text-xs font-semibold text-[#7A4986]">{t.label}</span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate font-semibold text-[#373A45]">{t.title}</span>
                    <span className="block text-xs text-[#6B7280]">{day(t.created_at)}</span>
                  </span>
                  <Chip tone={REPORT_STATUS[t.status]?.tone}>{REPORT_STATUS[t.status]?.label || t.status}</Chip>
                  <ChevronRight size={16} className="shrink-0 text-[#9CA3AF]" />
                </Link>
              </li>
            ))}
          </ul>
        )
      )}
    </Shell>
  );
}

const EMPTY = { kind: "", title: "", description: "", steps: "", impact: "" };

function ReportForm({ from, start, onClose, onSent }) {
  const [form, setForm] = useState(() => ({ ...EMPTY, ...(start || {}) }));
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const box = useRef(null);
  const navigate = useNavigate();
  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.kind) { toast.error("Alege ce vrei să ne spui."); return; }
    if (!form.impact) { toast.error("Alege cât de tare te încurcă."); return; }
    setBusy(true);
    try {
      const r = await api.post("/portal/tickets", {
        ...form, group_id: groupFromPath(from),
        context: {
          page: from, browser: browserLabel(window.navigator.userAgent),
          screen: `${window.innerWidth}×${window.innerHeight}`, site_version: process.env.REACT_APP_COMMIT || "",
        },
      });
      if (files.length) await uploadScreenshots(r.data.id, files, { base: "/portal/tickets" });
      toast.success(sentLine(r.data.label));
      onSent();
      navigate(`/portal/raportari/${r.data.id}`);
    } catch (err) {
      toast.error(apiErrorMessage(err, "Raportarea nu a putut fi trimisă."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form ref={box} onSubmit={submit} className="mt-6 space-y-5 rounded-3xl border border-[#E5E7EB] bg-white p-6 sm:p-8"
      data-testid="portal-report-form">
      <h2 className="font-display text-xl font-bold text-[#373A45]">Raportează o problemă</h2>

      <fieldset>
        <legend className="mb-2 text-sm font-semibold text-[#373A45]">Ce vrei să ne spui</legend>
        <div className="flex flex-wrap gap-2">
          {REPORT_KIND_ORDER.map((key) => (
            <Pick key={key} name="report-kind" value={key} checked={form.kind === key} onChange={set("kind")}
              label={REPORT_KIND[key].label} />
          ))}
        </div>
      </fieldset>

      <label className="block">
        <span className="mb-1 block text-sm font-semibold text-[#373A45]">Pe scurt, ce s-a întâmplat</span>
        <input value={form.title} onChange={set("title")} maxLength={160} required className={fieldCls} data-testid="portal-report-title" />
        <span className="mt-1 block text-xs text-[#6B7280]">De exemplu: „Nu pot încărca tema în PDF”</span>
      </label>

      <label className="block">
        <span className="mb-1 block text-sm font-semibold text-[#373A45]">{REPORT_KIND[form.kind]?.describe || "Ce s-a întâmplat"}</span>
        <textarea value={form.description} onChange={set("description")} rows={4} maxLength={5000} required className={fieldCls}
          data-testid="portal-report-description" />
      </label>

      {form.kind === "bug" && (
        <label className="block">
          <span className="mb-1 block text-sm font-semibold text-[#373A45]">Ce ai făcut înainte să apară problema (opțional)</span>
          <textarea value={form.steps} onChange={set("steps")} rows={3} maxLength={3000} className={fieldCls} />
          <span className="mt-1 block text-xs text-[#6B7280]">De exemplu: am deschis Tema 2, am ales fișierul, am apăsat Predă</span>
        </label>
      )}

      <fieldset>
        <legend className="mb-2 text-sm font-semibold text-[#373A45]">Cât de tare te încurcă</legend>
        <div className="flex flex-wrap gap-2">
          {REPORT_IMPACT_ORDER.map((key) => (
            <Pick key={key} name="report-impact" value={key} checked={form.impact === key} onChange={set("impact")}
              label={REPORT_IMPACT[key]} />
          ))}
        </div>
      </fieldset>

      <div>
        <span className="mb-1 block text-sm font-semibold text-[#373A45]">Capturi de ecran (opțional)</span>
        <p className="mb-2 rounded-xl bg-[#d99a2b]/10 px-3 py-2 text-xs leading-relaxed text-[#8a5d12]">{SCREENSHOT_WARNING}</p>
        <ScreenshotPicker files={files} onChange={setFiles} pasteTarget={box} />
      </div>

      <div className="flex justify-end gap-2">
        <button type="button" onClick={onClose} disabled={busy} className={secondaryBtn}>Renunță</button>
        <button type="submit" disabled={busy} className={primaryBtn} data-testid="portal-report-send">
          {busy && <Loader2 size={16} className="animate-spin" />} Trimite
        </button>
      </div>
    </form>
  );
}

function Pick({ name, value, checked, onChange, label }) {
  return (
    <label className={`cursor-pointer rounded-full border px-4 py-2 text-sm font-semibold transition-colors focus-within:ring-2 focus-within:ring-[#965EA7]
      ${checked ? "border-[#373A45] bg-[#373A45] text-white" : "border-[#E5E7EB] bg-white text-[#373A45] hover:border-[#965EA7]"}`}>
      <input type="radio" name={name} value={value} checked={checked} onChange={onChange} className="sr-only" />
      {label}
    </label>
  );
}
